import { del, get, patch, post } from "@/services/axiosUitils";

const basePath = "/pts_app/expense-summary";

/** Backend stores the US currency as "US", not "USD". */
export type ExpenseCurrency = "INR" | "US";

export type ExpenseFrequency = "Monthly" | "Yearly" | "One Time" | string;

export type ExpenseStatus = "Active" | "Cancelled" | "Paused" | string;

export interface ExpenseItem {
  id: string;
  title: string;
  category: string;
  vendor: string;
  frequency: ExpenseFrequency;
  billing_date: string;
  quantity: number | string;
  currency: ExpenseCurrency | string;
  unit_cost: number | string;
  total_expense: number | string;
  status: ExpenseStatus;
  is_recurring?: boolean;
  created_on?: string;
  updated_on?: string;
}

export interface ExpensePayload {
  title: string;
  category: string;
  vendor: string;
  frequency: ExpenseFrequency;
  billing_date: string;
  quantity: number;
  currency: ExpenseCurrency;
  unit_cost: number;
  total_expense: number;
  status?: ExpenseStatus;
  is_recurring?: boolean;
}

export interface ExpenseListParams {
  page?: number;
  page_size?: number;
  month?: string;
  year?: string | number;
  category?: string;
  frequency?: string;
  status?: string;
  search?: string;
}

/**
 * Query params sent to the API. `month`/`year` are wrapped in a JSON `filters`
 * param; everything else is passed flat.
 */
interface ExpenseListQuery {
  filters?: string;
  category?: string;
  frequency?: string;
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

function buildListQuery(params?: ExpenseListParams): ExpenseListQuery {
  const { month, year, category, frequency, status, search, page, page_size } =
    params ?? {};

  const filters: Record<string, string | number> = {};
  if (month) {
    filters.month = month;
  }
  if (year !== undefined && year !== null && year !== "") {
    filters.year = Number(year);
  }

  const query: ExpenseListQuery = {};
  if (Object.keys(filters).length > 0) {
    query.filters = JSON.stringify(filters);
  }
  if (category) {
    query.category = category;
  }
  if (frequency) {
    query.frequency = frequency;
  }
  if (status) {
    query.status = status;
  }
  if (search) {
    query.search = search;
  }
  if (page !== undefined) {
    query.page = page;
  }
  if (page_size !== undefined) {
    query.page_size = page_size;
  }

  return query;
}

export interface ExpenseListResult {
  results: ExpenseItem[];
  count: number;
}

function unwrapList(response: unknown): ExpenseItem[] {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response as ExpenseItem[];
  }

  const data = response as Record<string, unknown>;
  if (Array.isArray(data.results)) {
    return data.results as ExpenseItem[];
  }
  if (Array.isArray(data.data)) {
    return data.data as ExpenseItem[];
  }

  return [];
}

function unwrapCount(response: unknown, fallback: number): number {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return fallback;
  }
  const data = response as Record<string, unknown>;
  if (typeof data.count === "number") {
    return data.count;
  }
  if (typeof data.total === "number") {
    return data.total;
  }
  return fallback;
}

class PtsExpenseSummaryService {
  /**
   * List expenses.
   * Calls: GET /pts_app/expense-summary/
   */
  getExpenses = async (
    params?: ExpenseListParams
  ): Promise<ExpenseListResult> => {
    const response = await get<unknown>(`${basePath}/`, {
      params: buildListQuery(params),
    });
    const results = unwrapList(response);
    return { results, count: unwrapCount(response, results.length) };
  };

  /**
   * Create an expense.
   * Calls: POST /pts_app/expense-summary/
   */
  createExpense = async (
    payload: ExpensePayload
  ): Promise<ExpenseItem | undefined> => {
    return await post<ExpenseItem>(`${basePath}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Update an expense.
   * Calls: PATCH /pts_app/expense-summary/{id}/
   */
  updateExpense = async (
    id: string,
    payload: Partial<ExpensePayload>
  ): Promise<ExpenseItem | undefined> => {
    return await patch<ExpenseItem>(`${basePath}/${id}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Delete an expense.
   * Calls: DELETE /pts_app/expense-summary/{id}/
   */
  deleteExpense = async (id: string): Promise<unknown> => {
    return await del(`${basePath}/${id}/`, { notifyMessage: true });
  };

  /**
   * Get expense summary dashboard metrics.
   * Calls: GET /pts_app/expense-summary/dashboard/
   */
  getDashboard = async (params: {
    month?: string;
    year?: number | string;
  }): Promise<any> => {
    const filters: Record<string, string | number> = {};
    if (params.month) {
      filters.month = params.month;
    }
    if (
      params.year !== undefined &&
      params.year !== null &&
      params.year !== ""
    ) {
      filters.year = Number(params.year);
    }
    const query: Record<string, string> = {};
    if (Object.keys(filters).length > 0) {
      query.filters = JSON.stringify(filters);
    }
    return await get<any>(`${basePath}/dashboard/`, {
      params: query,
    });
  };
  /**
   * Create recurring expenses for a given year.
   * Calls: GET /pts_app/expense-recurring/?year=<year>
   */
  createRecurringExpenses = async (year: number): Promise<any> => {
    return await get<any>(`/pts_app/expense-recurring/`, {
      params: { year },
    });
  };
}

const instance = new PtsExpenseSummaryService();
export default instance;
